import { useBrokerFeatureLoader } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Loading,
  Switch,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import styled from "@rentiohq/web-theme";
import React from "react";
import { Navigate } from "react-router-dom";
import {
  BOOKING_ANCHOR_KEY,
  BOOKING_ANCHORS,
  CRONOFY_CONNECTION_PATH,
  CRONOFY_STATUS_PATH,
  ICronofyStatus,
  readCronofyStatus,
  TBookingAnchor,
} from "utils/cronofy.utils";

// The theme's own tokens rather than two hex values, so this dropdown
// follows the design system the way the identical one on the property
// calendar does. neutral40 is #D0D0D0 and neutral0 is #FFFFFF, so nothing
// moves on screen; the colors just stop being written out twice.
const AnchorSelect = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.neutral40};
  background: ${({ theme }) => theme.colors.neutral0};
  font-size: 14px;
`;

export const ProfileCalendar = () => {
  const hasFetched = React.useRef(false);
  const { isFetchingBroker, hasFeature: hasCalendarSync } =
    useBrokerFeatureLoader(EBrokerFeature.CalendarSync);
  const [statusData, setStatusData] = React.useState<ICronofyStatus | null>(
    null,
  );
  // Kept beside the fetched status so the checkbox answers the click at once
  // rather than after a round trip, and is put back if the save fails.
  const [isSavingHideEmail, setIsSavingHideEmail] = React.useState(false);
  const [isSavingAnchor, setIsSavingAnchor] = React.useState(false);
  const [isFetchingStatus, setIsFetchingStatus] = React.useState(true);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");

    if (code && !hasFetched.current) {
      hasFetched.current = true;
      const redirectUri = window.location.origin + window.location.pathname;
      api
        .post("/cronofy/oauth-callback", {
          data: { code, redirectUri },
          isV2: true,
        })
        .then(() => {
          // Immediately wipe the ?code parameters and force a hard page sync
          // so the user visually sees the connection!
          window.location.href = window.location.pathname;
        })
        .catch((err: any) => {
          console.error("OAuth callback failed", err); // eslint-disable-line no-console
        });
    }
  }, []);

  const refetchStatus = React.useCallback(async () => {
    try {
      setIsFetchingStatus(true);
      const res = await api.get<any>(CRONOFY_STATUS_PATH, {
        isV2: true,
        params: { t: Date.now() },
      });
      setStatusData(res.data?.data);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    } finally {
      setIsFetchingStatus(false);
    }
  }, []);

  React.useEffect(() => {
    refetchStatus();
  }, [refetchStatus]);

  const handleConnectCalendar = async () => {
    try {
      const redirectUri = window.location.origin + window.location.pathname;
      const response = await api.get<any>(
        `/cronofy/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`,
        { isV2: true },
      );
      if (response.data?.data?.url) {
        window.location.href = response.data.data.url;
      } else if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Failed to generate Cronofy URL", error); // eslint-disable-line no-console
    }
  };

  /**
   * The whole connection status, which is what both the read and the save
   * return since d14f4f231. Taking the save's answer as the new state keeps
   * the screen on what was stored rather than on what was asked for, and it
   * is how a field this screen did not touch stays current.
   */
  const readStatus = readCronofyStatus;

  const handleToggleHideEmail = async (nextValue: boolean) => {
    const previous = statusData;

    // Shown immediately, because a checkbox that waits for the server reads as
    // broken. Put back below if the save fails, so the screen never claims a
    // setting that was not stored.
    setStatusData(current =>
      current ? { ...current, hideEmailFromVisitors: nextValue } : current,
    );
    setIsSavingHideEmail(true);

    try {
      const response = await api.patch<{ data?: ICronofyStatus }>(
        CRONOFY_CONNECTION_PATH,
        {
          isV2: true,
          data: { hideEmailFromVisitors: nextValue },
        },
      );

      const saved = readStatus(response);
      if (saved) {
        setStatusData(saved);
      }
    } catch (error) {
      setStatusData(previous);
      showAlert({ type: "error", error });
    } finally {
      setIsSavingHideEmail(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.delete("/cronofy/disconnect", { isV2: true });
      await refetchStatus();
    } catch (error) {
      showAlert({
        type: "error",
        error,
      });
    }
  };

  if (isFetchingBroker || isFetchingStatus) {
    return <Loading />;
  }

  if (!hasCalendarSync) {
    return <Navigate to="../personal" replace />;
  }

  /**
   * The employee's default for new visit blocks (DEV-3328).
   *
   * Stored on the user rather than on the calendar connection, which is why
   * it is shown whether or not a calendar is connected: since DEV-3320 a
   * colleague without one can still be given visit moments, so they need a
   * default as much as anyone.
   */
  const handleChangeAnchorDefault = async (next: TBookingAnchor) => {
    const previous = statusData?.visitBookingAnchorDefault;
    setStatusData(current =>
      current ? { ...current, visitBookingAnchorDefault: next } : current,
    );
    setIsSavingAnchor(true);

    try {
      const response = await api.patch<{ data?: ICronofyStatus }>(
        CRONOFY_CONNECTION_PATH,
        {
          isV2: true,
          data: { visitBookingAnchorDefault: next },
        },
      );

      const saved = readStatus(response);
      if (saved) {
        setStatusData(saved);
      }
    } catch (error) {
      setStatusData(current =>
        current ? { ...current, visitBookingAnchorDefault: previous } : current,
      );
      toast({
        heading: getLocalizedText("general.error"),
        variation: "error",
      });
    } finally {
      setIsSavingAnchor(false);
    }
  };

  const anchorDefault = statusData?.visitBookingAnchorDefault || "START";

  const isConnected = statusData?.isConnected;

  return (
    <div style={{ padding: "32px", maxWidth: "600px" }}>
      <h2
        style={{ marginBottom: "16px", fontSize: "24px", fontWeight: "bold" }}
      >
        {getLocalizedText("cronofy.profile.title")}
      </h2>
      <p style={{ marginBottom: "24px", lineHeight: "1.5", color: "#666" }}>
        {getLocalizedText("cronofy.profile.description")}
      </p>

      {/* Outside the connected branch on purpose: this one is stored on the
          user, and a colleague without a connected calendar can be given
          visit moments too (DEV-3320), so they need the default as much as
          anyone. */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ marginBottom: "4px" }}>
          <TextStyle>
            {getLocalizedText("cronofy.profile.booking_anchor.label")}
          </TextStyle>
        </div>
        <div style={{ marginBottom: "8px" }}>
          <TextStyle variation="subdued" size="small">
            {getLocalizedText("cronofy.profile.booking_anchor.info")}
          </TextStyle>
        </div>
        {/* A dropdown, like the one in the block form it prefills, so the
            two read as the same setting (Jonas, 2026-10-06). */}
        <AnchorSelect
          value={anchorDefault}
          disabled={isSavingAnchor}
          onChange={event =>
            handleChangeAnchorDefault(event.target.value as TBookingAnchor)
          }
        >
          {BOOKING_ANCHORS.map(anchor => (
            <option key={anchor} value={anchor}>
              {getLocalizedText(BOOKING_ANCHOR_KEY[anchor])}
            </option>
          ))}
        </AnchorSelect>
      </div>

      {isConnected ? (
        <div
          style={{
            background: "#E6F6F2",
            border: "1px solid #00A87A",
            padding: "16px",
            borderRadius: "6px",
          }}
        >
          <h3
            style={{
              color: "#00A87A",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            {getLocalizedText("cronofy.profile.active_sync")}
          </h3>
          <p style={{ marginBottom: "16px", color: "#333" }}>
            {statusData.email
              ? getLocalizedText("cronofy.profile.synced_account", {
                  email: statusData.email,
                })
              : getLocalizedText("cronofy.profile.securely_connected")}
          </p>
          {/* Only while a calendar is connected: without one there is no
              connection to store the setting on, and the API answers 422.
              Disconnecting drops it, so a reconnect starts from off. */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <Switch
              isChecked={!!statusData?.hideEmailFromVisitors}
              isDisabled={isSavingHideEmail}
              onChange={handleToggleHideEmail}
            />
            <div>
              <div style={{ color: "#333" }}>
                {getLocalizedText("cronofy.profile.hide_email.label")}
              </div>
              <div style={{ color: "#666", fontSize: "13px" }}>
                {getLocalizedText("cronofy.profile.hide_email.info")}
              </div>
            </div>
          </div>

          <button
            onClick={handleDisconnect}
            style={{
              padding: "10px 20px",
              cursor: "pointer",
              background: "transparent",
              color: "#DC2626",
              border: "1px solid #DC2626",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {getLocalizedText("cronofy.profile.button.disconnect")}
          </button>
        </div>
      ) : (
        <button
          onClick={handleConnectCalendar}
          style={{
            padding: "12px 24px",
            cursor: "pointer",
            background: "#00A87A",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {getLocalizedText("cronofy.profile.button.connect")}
        </button>
      )}
    </div>
  );
};
